import { ChevronDown, ChevronUp } from "lucide-react";

import {
  CAMPOS_FISCAIS,
  CelulaFiscal,
  type Corrigir,
  type EstadoGravacao,
  IndicadorGravacao,
  listarCorrecoes,
} from "@/components/fiscal/CorrecaoFiscal";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { NotaFiscal, ReferenciaModelos, SubstituicoesNfs } from "@/types";

const COLUNAS = [
  "NF",
  "Operação",
  "CFOP",
  "ICMS",
  "CST",
  "PIS/COFINS",
  "CST",
  "IPI",
  "CST",
  "Informações complementares",
  "Frete",
  "Observação",
];

/** Destaca os tratamentos que não são alíquota — é onde mora o risco fiscal. */
const corIcms = (icms: string | null) =>
  icms === "Diferido" || icms === "Suspenso"
    ? "text-cyan-700"
    : icms === "Não incide"
      ? "text-emerald-700"
      : "text-foreground";

/** `par` é "TIPO_EMITENTE>TIPO_DESTINO"; o número da NF leva a cor da categoria do emitente, como no canvas. */
const tipoEmitente = (nf: NotaFiscal) => nf.par.split(">")[0];

export function RoteiroFiscal({
  nfs,
  tipoVisual,
  onMover,
  substituicoes,
  onCorrigir,
  gravacao = null,
  onTentarDeNovo,
}: {
  nfs: NotaFiscal[];
  tipoVisual?: ReferenciaModelos["tipoVisual"];
  /** Sobe (-1) ou desce (+1) a nota na ordem de emissão; sem ele, a ordem é fixa. */
  onMover?: (chave: string, direcao: -1 | 1) => void;
  /** Correções do Tax: o valor novo aparece com o calculado riscado abaixo. */
  substituicoes?: SubstituicoesNfs;
  /** Libera a correção dos valores fiscais (validação do Tax): clicar no valor abre o formulário. */
  onCorrigir?: Corrigir;
  /** Estado da gravação das correções, mostrado no cabeçalho. */
  gravacao?: EstadoGravacao;
  onTentarDeNovo?: () => void;
}) {
  const corrigidos = listarCorrecoes(substituicoes).length;

  if (!nfs.length) {
    return (
      <Card>
        <CardContent className="p-5 text-sm text-muted-foreground">
          Nenhuma nota a emitir ainda — crie ligações entre os nós para gerar o roteiro fiscal.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card id="roteiro-fiscal" className="scroll-mt-4">
      <CardHeader>
        <div className="flex flex-wrap items-baseline gap-2">
          <CardTitle>Roteiro fiscal — emissão de notas</CardTitle>
          {/* Ocupa o espaço livre: o selo fica centrado entre o título e o indicador. */}
          <span className="flex flex-1 justify-center">
            {corrigidos > 0 && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">
                {corrigidos} {corrigidos === 1 ? "valor corrigido" : "valores corrigidos"} pelo Tax
              </span>
            )}
          </span>
          {gravacao && <IndicadorGravacao estado={gravacao} onTentarDeNovo={onTentarDeNovo} />}
        </div>
        {!onCorrigir && (
          <CardDescription>
            Sequência de emissão conforme o fluxo desenhado. Confirme CSTs e dispositivos legais da
            UF com o fiscal.
          </CardDescription>
        )}
      </CardHeader>
      <CardContent>
        <Table className="min-w-[60rem]">
          <TableHeader>
            <TableRow>
              {onMover && (
                <TableHead className="nao-imprimir w-8 px-1">
                  <span className="sr-only">Ordem</span>
                </TableHead>
              )}
              {COLUNAS.map((coluna, i) => (
                <TableHead
                  key={`${coluna}-${i}`}
                  className={cn((i === 1 || i === 9 || i === 11) && "text-left")}
                >
                  {coluna}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {nfs.map((nf, i) => {
              const visual = tipoVisual?.[tipoEmitente(nf) as keyof typeof tipoVisual];
              return (
                <TableRow key={nf.chave || nf.n}>
                  {onMover && (
                    <TableCell className="nao-imprimir px-1 py-0">
                      <div className="flex flex-col items-center">
                        <button
                          type="button"
                          aria-label={`Subir a NF ${nf.n}`}
                          title="Emitir antes"
                          disabled={i === 0}
                          onClick={() => onMover(nf.chave, -1)}
                          className="rounded p-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
                        >
                          <ChevronUp className="size-4" />
                        </button>
                        <button
                          type="button"
                          aria-label={`Descer a NF ${nf.n}`}
                          title="Emitir depois"
                          disabled={i === nfs.length - 1}
                          onClick={() => onMover(nf.chave, 1)}
                          className="rounded p-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground disabled:pointer-events-none disabled:opacity-30"
                        >
                          <ChevronDown className="size-4" />
                        </button>
                      </div>
                    </TableCell>
                  )}
                  <TableCell className="p-0">
                    <div
                      className={cn(
                        "flex h-full min-h-10 w-8 items-center justify-center text-sm font-extrabold text-white",
                        !visual && "bg-fs-verde",
                      )}
                      style={visual && { background: visual.cor }}
                      title={visual && `Emitente: ${visual.nome}`}
                    >
                      {nf.n}
                    </div>
                  </TableCell>
                  <TableCell className="min-w-56">
                    <div className="font-extrabold text-fs-escuro">
                      {nf.rotulo} · {nf.emitente} → {nf.dest}
                    </div>
                    <div className="text-muted-foreground">{nf.operacao}</div>
                  </TableCell>
                  {CAMPOS_FISCAIS.map(({ campo, rotulo, className }) => (
                    <CelulaFiscal
                      key={campo}
                      nf={nf}
                      campo={campo}
                      rotulo={rotulo}
                      correcao={substituicoes?.[nf.chave]?.[campo]}
                      onCorrigir={onCorrigir}
                      className={cn(
                        "numerico",
                        className,
                        campo === "icms" && corIcms(nf.icms),
                      )}
                    />
                  ))}
                  <TableCell className="min-w-64 text-[11px] text-muted-foreground">
                    {nf.info}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-center">{nf.frete}</TableCell>
                  <TableCell className="min-w-32 text-[11px]">{nf.obs}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
